import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FilterIcon, 
  SearchIcon, 
  UserIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  XIcon,
  ChevronDownIcon 
} from 'lucide-react';
import { customerServices, Customer, supabase } from '../lib/supabase';

const Customers = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState<Partial<Customer> | null>(null);
  const [plans] = useState<string[]>(['Basic', 'Pro', 'Enterprise']);

  const handleCustomerClick = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await customerServices.getAll();
        
        if (error) {
          throw error;
        }
        
        if (data) {
          // Format the date if needed
          const formattedCustomers = data.map(customer => ({
            ...customer,
            join_date: customer.join_date ? new Date(customer.join_date as string).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            }) : 'N/A'
          }));
          
          setCustomers(formattedCustomers);
        }
      } catch (error: any) {
        setError(error.message || 'Failed to fetch customers');
        console.error('Error fetching customers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  // Open modal to add/edit customer
  const handleOpenModal = (customer: Customer | null = null) => {
    setCurrentCustomer(customer || {
      name: '',
      email: '',
      plan: 'Basic',
      status: 'Active',
      mrr: 0,
      health_score: 75,
    });
    setShowModal(true);
  };
  
  // Close modal and reset form
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentCustomer(null);
  };
  
  // Handle customer form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentCustomer || !currentCustomer.name || !currentCustomer.email) {
      setError('Please fill all required fields');
      return;
    }
    
    try {
      let result: { data: Customer[] | null; error: Error | null };
      
      if (currentCustomer.id) {
        // Update existing customer
        result = await customerServices.update(currentCustomer.id, currentCustomer);
        
        // Update local state
        setCustomers(prevCustomers => 
          prevCustomers.map(customer => 
            customer.id === currentCustomer.id 
              ? { ...customer, ...currentCustomer } 
              : customer
          )
        );
      } else {
        // Create new customer
        const newCustomer = {
          ...currentCustomer,
          join_date: new Date().toISOString(),
          user_id: (await supabase.auth.getUser()).data.user?.id, // Get the current authenticated user ID
        } as Customer;
        
        result = await customerServices.create(newCustomer);
        
        // Update local state
        if (result.data && result.data.length > 0) {
          const createdCustomer = {
            ...result.data[0],
            join_date: new Date(result.data[0].join_date as string).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            })
          };
          
          setCustomers(prevCustomers => [createdCustomer, ...prevCustomers]);
        }
      }
      
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };
  
  // Handle customer deletion
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await customerServices.delete(id);
      setCustomers(prevCustomers => prevCustomers.filter(customer => customer.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };
  
  // Filter customers based on search query
  const filteredCustomers = customers.filter(customer => {
    return (
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      customer.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Trial':
        return 'bg-blue-100 text-blue-800';
      case 'At Risk':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  return <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Customers</h1>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 md:flex-none">
            <input type="text" placeholder="Search customers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon size={16} className="text-gray-400" />
            </div>
          </div>
          <button 
            className="flex items-center px-4 py-2 border border-gray-300 bg-white rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <FilterIcon size={16} className="mr-2 text-gray-500" />
            Filters
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
          >
            <PlusIcon size={16} className="mr-2" />
            Add Customer
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-md flex justify-between items-center">
          <div>Error: {error}</div>
          <button 
            onClick={() => setError(null)} 
            className="text-red-700 hover:text-red-900"
          >
            <XIcon size={16} />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredCustomers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MRR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Join Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Health Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <UserIcon size={20} className="text-gray-500" />
                        </div>
                        <div className="ml-4">
                          <button 
                            onClick={() => customer.id && handleCustomerClick(customer.id)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 focus:outline-none"
                          >
                            {customer.name}
                          </button>
                          <div className="text-sm text-gray-500">
                            {customer.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{customer.plan}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(customer.status)}`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${customer.mrr}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.join_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-2 w-2 rounded-full ${getHealthScoreColor(customer.health_score)} mr-2`}></div>
                        <span className="text-sm text-gray-900">
                          {customer.health_score}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button 
                          onClick={() => handleOpenModal(customer)}
                          className="text-gray-400 hover:text-blue-600"
                          title="Edit customer"
                        >
                          <PencilIcon size={16} />
                        </button>
                        <button 
                          onClick={() => customer.id && handleDelete(customer.id)}
                          className="text-gray-400 hover:text-red-600"
                          title="Delete customer"
                        >
                          <TrashIcon size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No customers found. {searchQuery && "Try adjusting your search query."}
          </div>
        )}
      </div>

      {/* Add/Edit Customer Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
            <button 
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XIcon size={20} />
            </button>
            
            <h2 className="text-xl font-semibold mb-4">
              {currentCustomer?.id ? 'Edit Customer' : 'Add New Customer'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name*
                </label>
                <input
                  type="text"
                  value={currentCustomer?.name || ''}
                  onChange={(e) => setCurrentCustomer({
                    ...currentCustomer,
                    name: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email*
                </label>
                <input
                  type="email"
                  value={currentCustomer?.email || ''}
                  onChange={(e) => setCurrentCustomer({
                    ...currentCustomer,
                    email: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan
                </label>
                <div className="relative">
                  <select
                    value={currentCustomer?.plan || 'Basic'}
                    onChange={(e) => setCurrentCustomer({
                      ...currentCustomer,
                      plan: e.target.value
                    })}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    {plans.map(plan => (
                      <option key={plan} value={plan}>{plan}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronDownIcon size={16} />
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <div className="relative">
                  <select
                    value={currentCustomer?.status || 'Active'}
                    onChange={(e) => setCurrentCustomer({
                      ...currentCustomer,
                      status: e.target.value
                    })}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="Active">Active</option>
                    <option value="Trial">Trial</option>
                    <option value="At Risk">At Risk</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronDownIcon size={16} />
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Recurring Revenue (MRR)
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentCustomer?.mrr || 0}
                    onChange={(e) => setCurrentCustomer({
                      ...currentCustomer,
                      mrr: parseFloat(e.target.value)
                    })}
                    className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Health Score (0-100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={currentCustomer?.health_score || 75}
                  onChange={(e) => setCurrentCustomer({
                    ...currentCustomer,
                    health_score: parseInt(e.target.value, 10)
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {currentCustomer?.id ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>;
};

export default Customers;